/**
 * DirtyFormGuard — one mounted settings form may register its dirty lifecycle.
 * The registry intentionally holds one form only: route changes unmount the
 * current section, so a multi-form registry would model an impossible state.
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { COPY } from '@/lib/copyTokens';

export type DirtyFormSaveResult = boolean | { success: boolean; error?: string };

export interface DirtyFormRegistration {
  isDirty: () => boolean;
  save: () => DirtyFormSaveResult | Promise<DirtyFormSaveResult>;
  discard: () => void;
}

interface DirtyFormGuardRegistry {
  register: (form: DirtyFormRegistration) => () => void;
  isDirty: () => boolean;
  save: () => Promise<DirtyFormSaveResult>;
  discard: () => void;
}

function createDirtyFormGuardRegistry(): DirtyFormGuardRegistry {
  let currentForm: DirtyFormRegistration | null = null;

  return {
    register(form) {
      currentForm = form;
      return () => {
        if (currentForm === form) currentForm = null;
      };
    },
    isDirty: () => currentForm?.isDirty() ?? false,
    save: async () => currentForm?.save() ?? true,
    discard: () => currentForm?.discard(),
  };
}

// The settings shell is outside this executor's file scope. This shared
// default lets the rail and its one mounted section share the context today;
// the provider remains available if the shell later adopts an explicit scope.
const defaultRegistry = createDirtyFormGuardRegistry();
const DirtyFormGuardContext = createContext<DirtyFormGuardRegistry>(defaultRegistry);

export function DirtyFormGuardProvider({ children }: { children: ReactNode }) {
  const registryRef = useRef<DirtyFormGuardRegistry | null>(null);
  if (!registryRef.current) registryRef.current = createDirtyFormGuardRegistry();

  return (
    <DirtyFormGuardContext.Provider value={registryRef.current}>
      {children}
    </DirtyFormGuardContext.Provider>
  );
}

export function useDirtyFormGuard() {
  return useContext(DirtyFormGuardContext);
}

/** Registers the current section form and reliably removes it on unmount. */
export function useRegisterDirtyForm(registration: DirtyFormRegistration | null) {
  const guard = useDirtyFormGuard();

  useEffect(() => {
    if (!registration) return;
    return guard.register(registration);
  }, [guard, registration]);
}

function saveSucceeded(result: DirtyFormSaveResult) {
  return typeof result === 'boolean' ? result : result.success;
}

function saveError(result: DirtyFormSaveResult) {
  return typeof result === 'object' ? result.error : undefined;
}

interface DirtyFormGuardDialogProps {
  onStay: () => void;
  onDiscard: () => void;
  onSave: () => Promise<DirtyFormSaveResult>;
}

/**
 * The recovery dialog is deliberately owned by the navigation layer, so Save
 * can complete before the destination changes and a failure keeps the form
 * mounted. Section saves may return a specific error; false falls back to the
 * settings-wide recovery copy.
 */
export function DirtyFormGuardDialog({
  onStay,
  onDiscard,
  onSave,
}: DirtyFormGuardDialogProps) {
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    setError(null);
    try {
      const result = await onSave();
      if (saveSucceeded(result)) return;
      setError(saveError(result) ?? COPY.saveFailed);
    } catch (caught) {
      setError(caught instanceof Error && caught.message ? caught.message : COPY.saveFailed);
    } finally {
      setIsSaving(false);
    }
  }, [onSave]);

  return (
    <div
      aria-modal="true"
      aria-labelledby="dirty-form-guard-title"
      className="fixed inset-0 z-50 grid place-items-center bg-ink/30 px-4"
      role="alertdialog"
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-5 shadow-sm">
        <p id="dirty-form-guard-title" className="text-sm font-semibold text-ink">
          {COPY.cards.dirtyGuard.prompt}
        </p>
        {error ? (
          <p aria-live="assertive" className="mt-2 text-sm text-destructive">
            {error}
          </p>
        ) : null}
        <div className="mt-5 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={onStay}
            disabled={isSaving}
            className="rounded-md px-3 py-1.5 text-sm font-medium text-ink-2 hover:text-ink disabled:opacity-60"
          >
            {COPY.cards.dirtyGuard.stay}
          </button>
          <button
            type="button"
            onClick={onDiscard}
            disabled={isSaving}
            className="rounded-md border border-border px-3 py-1.5 text-sm font-medium text-ink-2 hover:text-ink disabled:opacity-60"
          >
            {COPY.cards.dirtyGuard.discard}
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="rounded-md bg-coral px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-coral-hover disabled:opacity-60"
          >
            {COPY.cards.dirtyGuard.save}
          </button>
        </div>
      </div>
    </div>
  );
}
